import { Link } from "react-router-dom";
import { Sparkles, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { HUME } from "@/lib/biohackerBrand";

/**
 * HumeStrip — reusable partnership + product-teaser strip.
 * Dropped into Landing, every Sport package page, /compliance, /platform.
 * Keeps the "Exclusive Official Athlete Intelligence Assessment Device™"
 * message visible everywhere without duplicating markup.
 */
export default function HumeStrip({ variant = "wide" }) {
  return (
    <section className="border-y border-white/8 bg-gradient-to-b from-black/40 to-[#050810]" data-testid="hume-strip">
      <div className={`max-w-7xl mx-auto px-6 py-16 grid ${variant === "wide" ? "lg:grid-cols-[1.05fr,0.95fr]" : "md:grid-cols-2"} gap-10 items-center`}>
        <div>
          <div className="text-[10px] tracking-[0.4em] uppercase font-bold text-fuchsia-300/80 mb-3 flex items-center gap-2">
            <Sparkles className="h-3.5 w-3.5" /> Partnership · Hume × BioHacker Sports™
          </div>
          <h2 className="font-display font-black text-3xl sm:text-4xl lg:text-5xl text-white tracking-tight leading-[1.05]">
            The Exclusive Official<br/>
            <span className="text-white/50">Athlete Intelligence Assessment Device™.</span>
          </h2>
          <p className="mt-5 text-white/70 max-w-xl leading-relaxed">{HUME.long}</p>
          <div className="mt-6 grid grid-cols-2 sm:grid-cols-3 gap-2" data-testid="hume-metric-tags">
            {HUME.metrics.map((g) => (
              <div key={g.group} className="rounded-lg border border-white/10 bg-white/[0.02] p-3">
                <div className="text-[9px] uppercase tracking-[0.25em] text-fuchsia-300/70 mb-1">{g.group}</div>
                <div className="text-[11px] text-white/70 leading-snug">{g.items.slice(0, 3).join(" · ")}</div>
              </div>
            ))}
          </div>
          <div className="mt-7 flex flex-wrap gap-3">
            <Link to="/shop/hume" data-testid="hume-strip-cta-shop">
              <Button className="text-white font-bold text-sm px-6 py-6" style={{ backgroundColor: "var(--brand-secondary)" }}>
                Shop the Hume Band — ${HUME.price_usd} <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
            <Link to="/platform" data-testid="hume-strip-cta-learn">
              <Button variant="ghost" className="text-white/90 border border-white/20 hover:bg-white/10 font-bold text-sm px-6 py-6">
                See how it plugs in
              </Button>
            </Link>
          </div>
        </div>

        <div className="relative">
          <div className="absolute -inset-6 bg-gradient-to-br from-fuchsia-500/10 via-transparent to-cyan-400/10 blur-3xl rounded-[3rem] pointer-events-none" />
          <div className="relative rounded-3xl overflow-hidden ring-1 ring-white/10 bg-black/40">
            <img
              src={HUME.images.hero}
              alt="Hume Band — official athlete intelligence assessment device"
              loading="lazy"
              className="w-full h-auto object-cover"
              data-testid="hume-strip-image"
            />
          </div>
          <div className="mt-3 flex gap-2 overflow-x-auto pb-1" data-testid="hume-strip-thumbs">
            {[HUME.images.wrist, HUME.images.brief, HUME.images.detail].map((src, i) => (
              <img key={i} src={src} alt="Hume Band detail" loading="lazy"
                className="h-16 w-24 object-cover rounded-md ring-1 ring-white/10 shrink-0" />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
